// Workflow tree model — 3 levels: stage → subtask → collected data.
export const WORKFLOW_STAGES = [
    {
        id: 'project_scoping',
        label: 'Project Scoping',
        subtasks: [{ id: 'collect_brief', label: 'Collect brief', stepId: 'project_scoping' }],
    },
    {
        id: 'target_biology',
        label: 'Target Biology',
        subtasks: [
            { id: 'collect_biology', label: 'Collect biology information', stepId: 'target_biology' },
        ],
    },
    {
        id: 'pdb_selection',
        label: 'PDB Selection',
        subtasks: [
            { id: 'choose_structure', label: 'Choose structure', stepId: 'pdb_selection' },
            { id: 'prepare_receptor', label: 'Prepare receptor', stepId: 'receptor_preparation' },
        ],
    },
    {
        id: 'binding_site',
        label: 'Binding Site Selection',
        subtasks: [
            { id: 'identify_pocket', label: 'Identify binding pocket', stepId: 'binding_site' },
        ],
    },
    {
        id: 'peptide_design',
        label: 'Peptide Design',
        subtasks: [{ id: 'generate_candidates', label: 'Generate candidates', stepId: 'peptide_design' }],
    },
];
function statusFromProgress(stepId, progressMap, currentStep) {
    const fromApi = progressMap.get(stepId);
    if (fromApi)
        return fromApi;
    if (currentStep === stepId)
        return 'active';
    return 'pending';
}
function subtaskStatusesForStep(subtasks, progressMap, currentStep) {
    return subtasks.map((sub) => statusFromProgress(sub.stepId, progressMap, currentStep));
}
function aggregateStatus(statuses) {
    if (!statuses.length)
        return 'pending';
    if (statuses.every((s) => s === 'done'))
        return 'done';
    if (statuses.some((s) => s === 'active'))
        return 'active';
    if (statuses.some((s) => s === 'done'))
        return 'active';
    return 'pending';
}
function progressPctFromStatuses(statuses) {
    if (!statuses.length)
        return 0;
    const done = statuses.filter((s) => s === 'done').length;
    const active = statuses.some((s) => s === 'active') ? 0.5 : 0;
    return Math.round(((done + active) / statuses.length) * 100);
}
function readBrief(data) {
    var _a;
    if (!data)
        return null;
    const snap = (_a = data.state_snapshot) === null || _a === void 0 ? void 0 : _a.project_brief;
    if (snap && typeof snap === 'object')
        return snap;
    if (data.project_brief)
        return data.project_brief;
    return null;
}
function detailValue(value) {
    const filled = value != null && value !== '';
    const str = filled ? String(value) : '';
    return { label: '', value: str, filled };
}
function briefField(value, label) {
    const filled = value != null && value !== '';
    return {
        label,
        value: filled ? String(value) : '—',
        filled,
    };
}
function extractDetailsForStage(stageId, _subtaskId, data) {
    var _a, _b, _c, _d, _e, _f, _g;
    const brief = readBrief(data);
    const target = data === null || data === void 0 ? void 0 : data.target_fiche;
    const binding = (_a = data === null || data === void 0 ? void 0 : data.binding_site) !== null && _a !== void 0 ? _a : (_b = data === null || data === void 0 ? void 0 : data.state_snapshot) === null || _b === void 0 ? void 0 : _b.binding_site;
    const candidates = (_c = data === null || data === void 0 ? void 0 : data.candidates) !== null && _c !== void 0 ? _c : [];
    const pdb = (_d = data === null || data === void 0 ? void 0 : data.selected_pdb) !== null && _d !== void 0 ? _d : (_e = data === null || data === void 0 ? void 0 : data.state_snapshot) === null || _e === void 0 ? void 0 : _e.selected_pdb;
    let items = [];
    switch (stageId) {
        case 'project_scoping':
            if (_subtaskId === 'collect_brief') {
                const constraints = Array.isArray(brief === null || brief === void 0 ? void 0 : brief.constraints)
                    ? brief.constraints.join(', ')
                    : brief === null || brief === void 0 ? void 0 : brief.constraints;
                return [
                    briefField(brief === null || brief === void 0 ? void 0 : brief.target, 'Target'),
                    briefField(brief === null || brief === void 0 ? void 0 : brief.pathology, 'Pathology'),
                    briefField(brief === null || brief === void 0 ? void 0 : brief.strategy, 'Strategy'),
                    briefField(constraints, 'Constraints'),
                ];
            }
            break;
        case 'target_biology':
            items = [
                Object.assign(Object.assign({}, detailValue((_f = target === null || target === void 0 ? void 0 : target.target) !== null && _f !== void 0 ? _f : brief === null || brief === void 0 ? void 0 : brief.target)), { label: 'Target' }),
                Object.assign(Object.assign({}, detailValue(target === null || target === void 0 ? void 0 : target.uniprot_id)), { label: 'UniProt' }),
                Object.assign(Object.assign({}, detailValue(target === null || target === void 0 ? void 0 : target.mechanism)), { label: 'Mechanism' }),
                Object.assign(Object.assign({}, detailValue(Array.isArray(target === null || target === void 0 ? void 0 : target.mutations) ? target.mutations.join(', ') : target === null || target === void 0 ? void 0 : target.mutations)), { label: 'Mutations' }),
            ];
            break;
        case 'pdb_selection':
            if (_subtaskId === 'prepare_receptor') {
                items = [Object.assign(Object.assign({}, detailValue(pdb ? `PDB ${pdb}` : null)), { label: 'Structure' })];
            }
            else {
                items = [Object.assign(Object.assign({}, detailValue(pdb)), { label: 'Selected PDB' })];
            }
            break;
        case 'binding_site':
            items = [
                Object.assign(Object.assign({}, detailValue((_g = binding === null || binding === void 0 ? void 0 : binding.pocket_id) !== null && _g !== void 0 ? _g : binding === null || binding === void 0 ? void 0 : binding.pdb_id)), { label: 'Pocket' }),
                Object.assign(Object.assign({}, detailValue(binding === null || binding === void 0 ? void 0 : binding.score)), { label: 'Score' }),
            ];
            break;
        case 'peptide_design':
            items = candidates.slice(0, 3).map((c, i) => {
                var _a, _b;
                return ({
                    label: `Candidate ${i + 1}`,
                    value: (_b = (_a = c.sequence) !== null && _a !== void 0 ? _a : c.id) !== null && _b !== void 0 ? _b : '—',
                    filled: true,
                });
            });
            break;
        default:
            break;
    }
    return items.filter((d) => d.filled);
}
function briefProgressPct(data) {
    const brief = readBrief(data);
    if (!brief)
        return 0;
    const confirmed = brief.confirmed_fields;
    if (Array.isArray(confirmed) && confirmed.length) {
        return Math.round((confirmed.length / 4) * 100);
    }
    const constraints = brief.constraints;
    const fields = [
        brief.target,
        brief.pathology,
        brief.strategy,
        Array.isArray(constraints) ? constraints.length : constraints,
    ];
    const filled = fields.filter((f) => f != null && f !== '' && f !== 0).length;
    return Math.round((filled / fields.length) * 100);
}
export function buildWorkflowTreeView(stepProgress, currentStep, data) {
    const progressMap = new Map();
    (stepProgress !== null && stepProgress !== void 0 ? stepProgress : []).forEach(({ step, status }) => {
        progressMap.set(step, status);
    });
    return WORKFLOW_STAGES.map((stage) => {
        const statuses = subtaskStatusesForStep(stage.subtasks, progressMap, currentStep);
        const status = aggregateStatus(statuses);
        let progressPct = progressPctFromStatuses(statuses);
        if (stage.id === 'project_scoping') {
            progressPct = briefProgressPct(data);
        }
        const subtasks = stage.subtasks.map((sub, i) => (Object.assign(Object.assign({}, sub), { status: statuses[i], details: extractDetailsForStage(stage.id, sub.id, data) })));
        return Object.assign(Object.assign({}, stage), { status,
            progressPct,
            subtasks, defaultExpanded: status === 'active' || status === 'done' });
    });
}
/** @deprecated Use buildWorkflowTreeView — kept for trace lookup compatibility. */
export function buildPipelineTreeView(stepProgress, currentStep) {
    return buildWorkflowTreeView(stepProgress, currentStep, null);
}
export function findTraceForStep(pipelineTrace, stepId) {
    if (!stepId || !(pipelineTrace === null || pipelineTrace === void 0 ? void 0 : pipelineTrace.length))
        return null;
    const matches = pipelineTrace.filter((t) => t.step === stepId);
    return matches.length ? matches[matches.length - 1] : null;
}
export function resolveSelectedStepId(currentStep, manualSelection) {
    if (manualSelection)
        return manualSelection;
    return currentStep !== null && currentStep !== void 0 ? currentStep : null;
}
